import { useState, useEffect } from 'react'
import { Button } from '@creanizer/ui'
import { migrationManager, type MigrationPlan } from '@creanizer/utils'

interface StoreInfo {
  name: string
  displayName: string
  description: string
  priority: number
}

const STORES: StoreInfo[] = [
  {
    name: 'settings-storage',
    displayName: '設定ストア',
    description: 'APIキーとアプリケーション設定',
    priority: 1
  },
  {
    name: 'name-store',
    displayName: '名前ストア',
    description: '生成された名前データ',
    priority: 2
  },
  {
    name: 'plot-store',
    displayName: 'プロットストア',
    description: 'プロットとストーリーデータ',
    priority: 2
  },
  {
    name: 'character-store',
    displayName: 'キャラクターストア',
    description: 'キャラクター情報',
    priority: 2
  },
  {
    name: 'worldbuilder-storage',
    displayName: '世界観ストア',
    description: '世界観ビルダーデータ',
    priority: 3
  },
  {
    name: 'enemy-store',
    displayName: 'エネミーストア',
    description: 'エネミーデータ',
    priority: 3
  },
  {
    name: 'title-store',
    displayName: 'タイトルストア',
    description: 'タイトル生成データ',
    priority: 3
  },
  {
    name: 'creanizer-wikipedias',
    displayName: '架空事典ストア',
    description: 'Wikipedia風記事データ',
    priority: 3
  },
  {
    name: 'imagePrompt-store',
    displayName: '画像プロンプトストア',
    description: '画像生成プロンプト',
    priority: 3
  }
]

export default function StorageMigration() {
  const [migrationPlans, setMigrationPlans] = useState<Map<string, MigrationPlan>>(new Map())
  const [isProcessing, setIsProcessing] = useState(false)
  const [logs, setLogs] = useState<string[]>([])
  const [selectedStore, setSelectedStore] = useState<string | null>(null)
  
  const appendLog = (message: string) => {
    setLogs(prev => [...prev, `[${new Date().toLocaleTimeString()}] ${message}`])
  }
  
  useEffect(() => {
    checkCurrentStatus()
  }, [])
  
  const checkCurrentStatus = () => {
    appendLog('📊 現在のストレージ状態を確認中...')
    
    const status = new Map<string, MigrationPlan>()
    
    STORES.forEach(store => {
      const localData = localStorage.getItem(store.name)
      if (localData) {
        status.set(store.name, {
          storeName: store.name,
          localStorageKey: store.name,
          currentDataSize: localData.length,
          backupCreated: false,
          status: 'pending'
        })
        appendLog(`✅ ${store.displayName}: ${localData.length} bytes`)
      } else {
        appendLog(`❌ ${store.displayName}: データなし`)
      }
    })
    
    setMigrationPlans(status)
  }
  
  const prepareMigration = async (storeName: string) => {
    setIsProcessing(true)
    
    try {
      appendLog(`🔧 ${storeName} の移行準備中...`)
      const plan = await migrationManager.prepareMigration(storeName)
      
      setMigrationPlans(prev => {
        const newPlans = new Map(prev)
        newPlans.set(storeName, plan)
        return newPlans
      })
      
      appendLog(`✅ ${storeName} の準備完了`)
    } catch (error) {
      appendLog(`❌ エラー: ${error}`)
    } finally {
      setIsProcessing(false)
    }
  }
  
  const executeMigration = async (storeName: string) => {
    if (!confirm(`${storeName} をIndexedDBに移行しますか？\n\nバックアップは作成済みですが、念のため重要なデータは手動でもバックアップしてください。`)) {
      return
    }
    
    setIsProcessing(true)
    
    try {
      appendLog(`🚀 ${storeName} の移行を開始...`)
      const result = await migrationManager.migrateStore(storeName)
      
      if (result.success) {
        appendLog(`✅ ${storeName} の移行成功！`)
        appendLog(`   データ整合性: ${result.dataIntegrity ? 'OK' : 'NG'}`)
      } else {
        appendLog(`❌ ${storeName} の移行失敗: ${result.error}`)
      }
      
      // ステータス更新
      checkCurrentStatus()
    } catch (error) {
      appendLog(`❌ エラー: ${error}`)
    } finally {
      setIsProcessing(false)
    }
  }
  
  const rollbackMigration = async (storeName: string) => {
    if (!confirm(`${storeName} の移行をロールバックしますか？`)) {
      return
    }
    
    setIsProcessing(true)
    
    try {
      appendLog(`⏪ ${storeName} のロールバック中...`)
      const success = await migrationManager.rollbackMigration(storeName)
      
      if (success) {
        appendLog(`✅ ${storeName} のロールバック成功`)
      } else {
        appendLog(`❌ ${storeName} のロールバック失敗`)
      }
      
      checkCurrentStatus()
    } catch (error) {
      appendLog(`❌ エラー: ${error}`)
    } finally {
      setIsProcessing(false)
    }
  }
  
  const testMigration = async (storeName: string) => {
    setIsProcessing(true)
    
    try {
      appendLog(`🧪 ${storeName} のテスト移行を開始...`)
      
      // 準備
      await migrationManager.prepareMigration(storeName)
      
      // 移行実行
      const result = await migrationManager.migrateStore(storeName)
      
      if (result.success && result.dataIntegrity) {
        appendLog(`✅ テスト成功！データ整合性OK`)
        
        // 成功した場合、すぐにロールバック（テストなので）
        appendLog(`⏪ テスト完了、ロールバック中...`)
        await migrationManager.rollbackMigration(storeName)
        appendLog(`✅ テスト完了`)
      } else {
        appendLog(`❌ テスト失敗: ${result.error}`)
      }
      
    } catch (error) {
      appendLog(`❌ テストエラー: ${error}`)
    } finally {
      setIsProcessing(false)
    }
  }
  
  return (
    <div className="max-w-6xl mx-auto p-6">
      <h1 className="text-2xl font-bold mb-4">ストレージ移行管理</h1>
      
      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
        <h2 className="text-lg font-semibold text-yellow-800 mb-2">⚠️ 重要な注意事項</h2>
        <ul className="list-disc list-inside text-yellow-700 space-y-1">
          <li>移行は1ストアずつ実行してください</li>
          <li>各ストアの移行前に必ずテストを実行してください</li>
          <li>問題が発生した場合はロールバック機能を使用してください</li>
          <li>バックアップは30日間保持されます</li>
        </ul>
      </div>
      
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div>
          <h2 className="text-lg font-semibold mb-4">ストア一覧</h2>
          
          <div className="space-y-3">
            {STORES.map(store => {
              const plan = migrationPlans.get(store.name)
              const hasData = !!plan
              
              return (
                <div
                  key={store.name}
                  className={`border rounded-lg p-4 ${
                    selectedStore === store.name ? 'border-blue-500 bg-blue-50' : 'border-gray-300'
                  }`}
                >
                  <div className="flex justify-between items-start mb-2">
                    <div>
                      <h3 className="font-semibold">{store.displayName}</h3>
                      <p className="text-sm text-gray-600">{store.description}</p>
                      {plan && (
                        <p className="text-xs text-gray-500 mt-1">
                          サイズ: {(plan.currentDataSize / 1024).toFixed(2)} KB
                        </p>
                      )}
                    </div>
                    <div className="flex items-center space-x-2">
                      {plan?.status === 'completed' && (
                        <span className="text-green-600">✅</span>
                      )}
                      {plan?.status === 'failed' && (
                        <span className="text-red-600">❌</span>
                      )}
                      {plan?.status === 'in_progress' && (
                        <span className="text-yellow-600">🔄</span>
                      )}
                    </div>
                  </div>
                  
                  {hasData && (
                    <div className="flex flex-wrap gap-2 mt-3">
                      <Button
                        size="sm"
                        variant="secondary"
                        onClick={() => testMigration(store.name)}
                        disabled={isProcessing}
                      >
                        🧪 テスト
                      </Button>
                      
                      {plan.backupCreated ? (
                        <>
                          <Button
                            size="sm"
                            variant="default"
                            onClick={() => executeMigration(store.name)}
                            disabled={isProcessing || plan.status === 'completed'}
                          >
                            🚀 移行実行
                          </Button>
                          
                          {plan.status === 'failed' && (
                            <Button
                              size="sm"
                              variant="destructive"
                              onClick={() => rollbackMigration(store.name)}
                              disabled={isProcessing}
                            >
                              ⏪ ロールバック
                            </Button>
                          )}
                        </>
                      ) : (
                        <Button
                          size="sm"
                          variant="secondary"
                          onClick={() => prepareMigration(store.name)}
                          disabled={isProcessing}
                        >
                          🔧 準備
                        </Button>
                      )}
                    </div>
                  )}
                </div>
              )
            })}
          </div>
        </div>
        
        <div>
          <h2 className="text-lg font-semibold mb-4">実行ログ</h2>
          
          <div className="bg-gray-900 text-green-400 rounded-lg p-4 h-96 overflow-y-auto font-mono text-sm">
            {logs.length === 0 ? (
              <div className="text-gray-500">ログがまだありません</div>
            ) : (
              logs.map((log, i) => (
                <div key={i} className="mb-1">
                  {log}
                </div>
              ))
            )}
          </div>
          
          <div className="mt-4 flex justify-between">
            <Button
              variant="secondary"
              onClick={() => setLogs([])}
              disabled={isProcessing}
            >
              ログクリア
            </Button>
            
            <Button
              variant="secondary"
              onClick={checkCurrentStatus}
              disabled={isProcessing}
            >
              🔄 状態を更新
            </Button>
          </div>
        </div>
      </div>
      
      {isProcessing && (
        <div className="fixed bottom-4 right-4 bg-blue-500 text-white px-4 py-2 rounded-lg shadow-lg">
          処理中...
        </div>
      )}
    </div>
  )
}